import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardList, Package, Plus, Wrench } from "lucide-react";

import { ApiError, formatCLP, formatDate } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { EmptyState } from "../../components/EmptyState";
import { Modal } from "../../components/Modal";
import {
  Badge,
  type BadgeTone,
  Button,
  Cargando,
  ErrorCarga,
  Input,
  PageHeader,
  Select,
  TableWrap,
  Textarea,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import { listarBodegas, listarInsumos } from "../inventario/api";
import { listarUsuarios } from "../usuarios/api";
import {
  agregarConsumo,
  cancelarOrden,
  crearOrden,
  esperaRepuestosOrden,
  finalizarOrden,
  iniciarOrden,
  listarActivos,
  listarOrdenes,
  numeroOT,
  obtenerOrden,
  quitarConsumo,
  ESTADO_OT_LABELS,
  PRIORIDAD_OT_LABELS,
  TIPO_OT_LABELS,
  type EstadoActivo,
  type EstadoOT,
  type OrdenTrabajoCreate,
  type OrdenTrabajoRead,
  type PrioridadOT,
  type TipoOT,
} from "./api";

const TIPOS: TipoOT[] = [
  "Preventivo",
  "Correctivo",
  "Emergencia",
  "Calibracion",
  "Instalacion",
];
const PRIORIDADES: PrioridadOT[] = ["Baja", "Media", "Alta", "Critica"];
const ESTADOS: EstadoOT[] = [
  "Pendiente",
  "Asignada",
  "EnProceso",
  "EsperaRepuestos",
  "Finalizada",
  "Cancelada",
];

const ESTADO_TONE: Record<EstadoOT, BadgeTone> = {
  Pendiente: "slate",
  Asignada: "brand",
  EnProceso: "amber",
  EsperaRepuestos: "amber",
  Finalizada: "emerald",
  Cancelada: "slate",
};
const TIPO_TONE: Record<TipoOT, BadgeTone> = {
  Preventivo: "brand",
  Correctivo: "amber",
  Emergencia: "rose",
  Calibracion: "slate",
  Instalacion: "emerald",
};
const PRIORIDAD_TONE: Record<PrioridadOT, BadgeTone> = {
  Baja: "slate",
  Media: "slate",
  Alta: "amber",
  Critica: "rose",
};

const nroOT = numeroOT;

export function OrdenesTrabajoPage(): JSX.Element {
  const [creando, setCreando] = useState(false);
  // `?ot=ID` abre el detalle: lo usa "Ver OT" desde una atención derivada.
  const [params, setParams] = useSearchParams();
  const otUrl = Number(params.get("ot"));
  const [detalle, setDetalleState] = useState<number | null>(
    Number.isInteger(otUrl) && otUrl > 0 ? otUrl : null,
  );
  function setDetalle(id: number | null): void {
    setDetalleState(id);
    if (id == null && params.has("ot")) {
      const sin = new URLSearchParams(params);
      sin.delete("ot");
      setParams(sin, { replace: true });
    }
  }
  const [filtroEstado, setFiltroEstado] = useState<EstadoOT | "">("");

  const ordenesQ = useQuery({
    queryKey: ["mantenimiento", "ordenes", filtroEstado || null],
    queryFn: () => listarOrdenes(filtroEstado ? { estado: filtroEstado } : undefined),
  });
  const activosQ = useQuery({ queryKey: ["mantenimiento", "activos"], queryFn: listarActivos });

  const activoNombre = useMemo(() => {
    const m = new Map<number, string>();
    for (const a of activosQ.data ?? []) m.set(a.id, a.nombre);
    return m;
  }, [activosQ.data]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Órdenes de trabajo"
        icon={Wrench}
        subtitle={ordenesQ.data && <span>({ordenesQ.data.length})</span>}
        actions={
          <Button icon={Plus} onClick={() => setCreando(true)}>
                    Nueva OT
                  </Button>
        }
      />

      <div className="flex items-center gap-2">
        <label className="text-sm text-neutral-600">Estado</label>
        <select
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value as EstadoOT | "")}
          className="rounded-md border border-neutral-450 px-3 py-1.5 text-sm shadow-sm"
        >
          <option value="">Todas</option>
          {ESTADOS.map((e) => (
            <option key={e} value={e}>
              {ESTADO_OT_LABELS[e]}
            </option>
          ))}
        </select>
      </div>

      {ordenesQ.isPending && <Cargando />}
      {ordenesQ.isError && (
        <ErrorCarga error={ordenesQ.error} onReintentar={() => ordenesQ.refetch()} />
      )}

      {ordenesQ.data && ordenesQ.data.length === 0 && (
        <EmptyState
          icon={ClipboardList}
          title="Sin órdenes de trabajo"
          description="Registra un trabajo de mantenimiento (correctivo, preventivo, emergencia) sobre un activo del comité."
        />
      )}

      {ordenesQ.data && ordenesQ.data.length > 0 && (
        <TableWrap label="Órdenes de trabajo">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">N°</th>
                <th scope="col" className="px-3 py-2 text-left">Tipo</th>
                <th scope="col" className="px-3 py-2 text-left">Prioridad</th>
                <th scope="col" className="px-3 py-2 text-left">Descripción</th>
                <th scope="col" className="px-3 py-2 text-left">Activo</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
                <th scope="col" className="px-3 py-2 text-right">Costo</th>
                <th scope="col" className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {ordenesQ.data.map((o) => (
                <tr key={o.id} className="hover:bg-neutral-50">
                  <td className="px-3 py-2 whitespace-nowrap tabular-nums text-neutral-700">{nroOT(o)}</td>
                  <td className="px-3 py-2">
                    <Badge tone={TIPO_TONE[o.tipo]}>{TIPO_OT_LABELS[o.tipo]}</Badge>
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={PRIORIDAD_TONE[o.prioridad]}>{PRIORIDAD_OT_LABELS[o.prioridad]}</Badge>
                  </td>
                  <td className="px-3 py-2 max-w-xs truncate text-neutral-700">
                    {o.descripcion_solicitud}
                  </td>
                  <td className="px-3 py-2 text-neutral-600">
                    {o.activo_id != null
                      ? (activoNombre.get(o.activo_id) ?? "—")
                      : o.medidor_id != null
                      ? `Medidor #${o.medidor_id}`
                      : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={ESTADO_TONE[o.estado]}>{ESTADO_OT_LABELS[o.estado]}</Badge>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCLP(o.costo_total)}</td>
                  <td className="px-3 py-2 text-right">
                    <Button variant="link" size="sm" onClick={() => setDetalle(o.id)}>
                      Ver / gestionar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      {creando && <CrearOrdenModal onClose={() => setCreando(false)} />}
      {detalle != null && (
        <OrdenDetailModal ordenId={detalle} onClose={() => setDetalle(null)} />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Crear OT
// ────────────────────────────────────────────────────────────────────────

function CrearOrdenModal({ onClose }: { onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();

  const [tipo, setTipo] = useState<TipoOT>("Correctivo");
  const [prioridad, setPrioridad] = useState<PrioridadOT>("Media");
  const [descripcion, setDescripcion] = useState("");
  const [activoId, setActivoId] = useState("");
  const [asignadoId, setAsignadoId] = useState("");
  const [fechaProgramada, setFechaProgramada] = useState("");
  const [error, setError] = useState<string | null>(null);

  const activosQ = useQuery({ queryKey: ["mantenimiento", "activos"], queryFn: listarActivos });
  // El listado de usuarios puede requerir permiso que el operador no tenga:
  // lo cargamos best-effort y, si falla, no ofrecemos asignación.
  const usuariosQ = useQuery({
    queryKey: ["usuarios", "activos"],
    queryFn: () => listarUsuarios(true),
    retry: false,
  });

  const mut = useMutation({
    mutationFn: (p: OrdenTrabajoCreate) => crearOrden(p),
    onSuccess: (o) => {
      toast.success(`OT ${nroOT(o)} creada`);
      void qc.invalidateQueries({ queryKey: ["mantenimiento", "ordenes"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo crear";
      setError(msg);
      toast.error(msg);
    },
  });

  function submit(): void {
    setError(null);
    if (!descripcion.trim()) return setError("Describe el trabajo a realizar");
    const p: OrdenTrabajoCreate = { tipo, prioridad, descripcion_solicitud: descripcion.trim() };
    if (activoId) p.activo_id = Number(activoId);
    if (asignadoId) p.asignado_a_id = Number(asignadoId);
    if (fechaProgramada) p.fecha_programada = fechaProgramada;
    mut.mutate(p);
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios(
    [
      tipo, prioridad, descripcion, activoId, asignadoId, fechaProgramada,
    ],
  );
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Nueva orden de trabajo"
      icon={Wrench}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={mut.isPending}>
            {mut.isPending ? "Creando…" : "Crear OT"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Select label="Tipo" value={tipo} onChange={(e) => setTipo(e.target.value as TipoOT)}>
          {TIPOS.map((t) => (
            <option key={t} value={t}>
              {TIPO_OT_LABELS[t]}
            </option>
          ))}
        </Select>
        <Select
          label="Prioridad"
          value={prioridad}
          onChange={(e) => setPrioridad(e.target.value as PrioridadOT)}
        >
          {PRIORIDADES.map((p) => (
            <option key={p} value={p}>
              {PRIORIDAD_OT_LABELS[p]}
            </option>
          ))}
        </Select>
        <Select label="Activo (opcional)" value={activoId} onChange={(e) => setActivoId(e.target.value)}>
          <option value="">— Sin activo —</option>
          {(activosQ.data ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              {a.nombre}
            </option>
          ))}
        </Select>
        {usuariosQ.data && usuariosQ.data.length > 0 && (
          <Select
            label="Asignar a (opcional)"
            value={asignadoId}
            onChange={(e) => setAsignadoId(e.target.value)}
          >
            <option value="">— Sin asignar —</option>
            {usuariosQ.data.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nombre}
              </option>
            ))}
          </Select>
        )}
        <Input
          label="Fecha programada (opcional)"
          type="date"
          value={fechaProgramada}
          onChange={(e) => setFechaProgramada(e.target.value)}
        />
      </div>
      <div className="mt-3">
        <Textarea
          label="Descripción del trabajo"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          rows={3}
        />
      </div>
      {error && (
        <p className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </p>
      )}
      {dialogoDescarte}
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Detalle + ciclo de vida
// ────────────────────────────────────────────────────────────────────────

function OrdenDetailModal({
  ordenId,
  onClose,
}: {
  ordenId: number;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [subaccion, setSubaccion] = useState<"finalizar" | "cancelar" | "consumo" | null>(null);
  const [quitando, setQuitando] = useState<number | null>(null);
  const [motivoQuitar, setMotivoQuitar] = useState("");

  const otQ = useQuery({
    queryKey: ["mantenimiento", "orden", ordenId],
    queryFn: () => obtenerOrden(ordenId),
  });

  function invalidar(): void {
    void qc.invalidateQueries({ queryKey: ["mantenimiento", "orden", ordenId] });
    void qc.invalidateQueries({ queryKey: ["mantenimiento", "ordenes"] });
    void qc.invalidateQueries({ queryKey: ["inventario"] });
  }

  const quitarMut = useMutation({
    mutationFn: (args: { consumoId: number; motivo: string }) =>
      quitarConsumo(ordenId, args.consumoId, args.motivo),
    onSuccess: () => {
      toast.success("Material devuelto a bodega");
      setQuitando(null);
      setMotivoQuitar("");
      invalidar();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "No se pudo devolver"),
  });

  const puedeConsumir =
    otQ.data?.estado === "Asignada" ||
    otQ.data?.estado === "EnProceso" ||
    otQ.data?.estado === "EsperaRepuestos";

  const transicion = useMutation({
    mutationFn: (fn: () => Promise<OrdenTrabajoRead>) => fn(),
    onSuccess: () => {
      invalidar();
      toast.success("OT actualizada");
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "No se pudo actualizar"),
  });

  const ot = otQ.data;

  return (
    <Modal
      open
      onClose={onClose}
      title={ot ? `OT ${nroOT(ot)}` : "Orden de trabajo"}
      subtitle={ot ? `${TIPO_OT_LABELS[ot.tipo]} · Prioridad ${PRIORIDAD_OT_LABELS[ot.prioridad].toLowerCase()}` : undefined}
      icon={Wrench}
      size="lg"
    >
      {!ot ? (
        <Cargando inline />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={ESTADO_TONE[ot.estado]}>{ESTADO_OT_LABELS[ot.estado]}</Badge>
            {ot.fecha_inicio_real && (
              <span className="text-xs text-neutral-500">
                Iniciada {formatDate(ot.fecha_inicio_real)}
              </span>
            )}
            {ot.fecha_fin_real && (
              <span className="text-xs text-neutral-500">
                Finalizada {formatDate(ot.fecha_fin_real)}
              </span>
            )}
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
              Descripción
            </div>
            <p className="text-sm text-neutral-700">{ot.descripcion_solicitud}</p>
          </div>

          {ot.informe_tecnico && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
                Informe técnico
              </div>
              <p className="text-sm text-neutral-700">{ot.informe_tecnico}</p>
            </div>
          )}
          {ot.motivo_cancelacion && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
                Motivo de cancelación
              </div>
              <p className="text-sm text-neutral-700">{ot.motivo_cancelacion}</p>
            </div>
          )}

          {/* Costos */}
          <div className="grid grid-cols-3 gap-2 rounded-md border border-neutral-200 bg-neutral-50 p-3 text-center text-sm">
            <div>
              <div className="text-xs text-neutral-500">Materiales</div>
              <div className="font-semibold tabular-nums">{formatCLP(ot.costo_materiales)}</div>
            </div>
            <div>
              <div className="text-xs text-neutral-500">Mano de obra</div>
              <div className="font-semibold tabular-nums">{formatCLP(ot.costo_mano_obra)}</div>
            </div>
            <div>
              <div className="text-xs text-neutral-500">Total</div>
              <div className="font-semibold tabular-nums">{formatCLP(ot.costo_total)}</div>
            </div>
          </div>

          {/* Consumos */}
          <div>
            <div className="mb-1 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
                Materiales consumidos
              </div>
              {(ot.estado === "Asignada" ||
                ot.estado === "EnProceso" ||
                ot.estado === "EsperaRepuestos") && (
                <Button
                  variant="link"
                  size="sm"
                  icon={Package}
                  onClick={() => setSubaccion("consumo")}
                >
                  Agregar
                </Button>
              )}
            </div>
            {ot.consumos.length === 0 ? (
              <p className="text-sm text-neutral-500">Sin materiales consumidos.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {ot.consumos.map((c) => (
                  <li key={c.id} className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-neutral-700">
                        Insumo #{c.insumo_id} · {Number(c.cantidad)}
                      </span>
                      <span className="ml-auto tabular-nums text-neutral-500">
                        {formatCLP(c.costo_total)}
                      </span>
                      {puedeConsumir && quitando !== c.id && (
                        <Button
                          size="sm"
                          variant="linkDanger"
                          onClick={() => setQuitando(c.id)}
                          disabled={quitarMut.isPending}
                        >
                          Devolver
                        </Button>
                      )}
                    </div>
                    {quitando === c.id && (
                      <div className="flex items-end gap-2">
                        <Input
                          label="Motivo de la devolución"
                          value={motivoQuitar}
                          onChange={(e) => setMotivoQuitar(e.target.value)}
                          containerClassName="flex-1"
                        />
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            if (!motivoQuitar.trim()) {
                              toast.error("Indica el motivo");
                              return;
                            }
                            quitarMut.mutate({ consumoId: c.id, motivo: motivoQuitar.trim() });
                          }}
                          disabled={quitarMut.isPending}
                        >
                          Confirmar
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => setQuitando(null)}>
                          Cancelar
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {ot.consumos.length > 0 && puedeConsumir && (
              <p className="mt-1 text-xs text-neutral-500">
                Una OT con material consumido no se cancela: devuelve el material o finalízala
                con informe.
              </p>
            )}
          </div>

          {/* Acciones de ciclo de vida */}
          <div className="flex flex-wrap gap-2 border-t border-neutral-100 pt-3">
            {ot.estado === "Pendiente" && (
              <Button
                size="sm"
                onClick={() => transicion.mutate(() => iniciarOrden(ot.id))}
                disabled={transicion.isPending}
              >
                Iniciar
              </Button>
            )}
            {ot.estado === "Asignada" && (
              <Button
                size="sm"
                onClick={() => transicion.mutate(() => iniciarOrden(ot.id))}
                disabled={transicion.isPending}
              >
                Iniciar
              </Button>
            )}
            {ot.estado === "EnProceso" && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => transicion.mutate(() => esperaRepuestosOrden(ot.id))}
                disabled={transicion.isPending}
              >
                Esperar repuestos
              </Button>
            )}
            {ot.estado === "EsperaRepuestos" && (
              <Button
                size="sm"
                onClick={() => transicion.mutate(() => iniciarOrden(ot.id))}
                disabled={transicion.isPending}
              >
                Reanudar
              </Button>
            )}
            {(ot.estado === "Asignada" || ot.estado === "EnProceso") && (
              <Button size="sm" variant="primary" onClick={() => setSubaccion("finalizar")}>
                Finalizar
              </Button>
            )}
            {ot.estado !== "Finalizada" && ot.estado !== "Cancelada" && (
              <Button
                size="sm"
                variant="linkDanger"
                onClick={() => setSubaccion("cancelar")}
              >
                Cancelar OT
              </Button>
            )}
          </div>

          {subaccion === "finalizar" && (
            <FinalizarForm
              ordenId={ot.id}
              tieneActivo={ot.activo_id != null}
              onDone={() => {
                setSubaccion(null);
                invalidar();
              }}
              onCancel={() => setSubaccion(null)}
            />
          )}
          {subaccion === "cancelar" && (
            <CancelarForm
              ordenId={ot.id}
              onDone={() => {
                setSubaccion(null);
                invalidar();
              }}
              onCancel={() => setSubaccion(null)}
            />
          )}
          {subaccion === "consumo" && (
            <ConsumoForm
              ordenId={ot.id}
              onDone={() => {
                setSubaccion(null);
                invalidar();
              }}
              onCancel={() => setSubaccion(null)}
            />
          )}
        </div>
      )}
    </Modal>
  );
}

type EstadoActivoFinal = Exclude<EstadoActivo, "Baja">;

function FinalizarForm({
  ordenId,
  tieneActivo,
  onDone,
  onCancel,
}: {
  ordenId: number;
  tieneActivo: boolean;
  onDone: () => void;
  onCancel: () => void;
}): JSX.Element {
  const toast = useToast();
  const [informe, setInforme] = useState("");
  const [manoObra, setManoObra] = useState("0");
  const [servicios, setServicios] = useState("0");
  // Un trabajo terminado normalmente deja el activo operativo; el técnico
  // puede decir que no (Falla) o que sigue en mantención.
  const [estadoActivo, setEstadoActivo] = useState<EstadoActivoFinal | "">(
    tieneActivo ? "Operativo" : "",
  );

  const mut = useMutation({
    mutationFn: () =>
      finalizarOrden(ordenId, {
        informe_tecnico: informe.trim(),
        costo_mano_obra: Number(manoObra) || 0,
        costo_servicios: Number(servicios) || 0,
        ...(tieneActivo && estadoActivo ? { estado_activo: estadoActivo } : {}),
      }),
    onSuccess: () => {
      toast.success("OT finalizada");
      onDone();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "No se pudo finalizar"),
  });

  return (
    <div className="space-y-2 rounded-md border border-brand-200 bg-brand-50 p-3">
      <div className="text-sm font-medium text-brand-900">Finalizar OT</div>
      <Textarea
        label="Informe técnico (qué se hizo)"
        value={informe}
        onChange={(e) => setInforme(e.target.value)}
        rows={2}
      />
      <div className="grid grid-cols-2 gap-2">
        <Input
          label="Costo mano de obra $"
          type="number"
          min={0}
          value={manoObra}
          onChange={(e) => setManoObra(e.target.value)}
        />
        <Input
          label="Costo servicios $"
          type="number"
          min={0}
          value={servicios}
          onChange={(e) => setServicios(e.target.value)}
        />
      </div>
      {tieneActivo && (
        <Select
          label="El activo queda en"
          value={estadoActivo}
          onChange={(e) => setEstadoActivo(e.target.value as EstadoActivoFinal | "")}
        >
          <option value="Operativo">Operativo (reparado)</option>
          <option value="EnMantencion">En mantención (sigue en trabajo)</option>
          <option value="Falla">Falla (no se pudo reparar)</option>
          <option value="">No cambiar</option>
        </Select>
      )}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="secondary" onClick={onCancel} disabled={mut.isPending}>
          Cancelar
        </Button>
        <Button
          size="sm"
          onClick={() => {
            if (!informe.trim()) {
              toast.error("El informe técnico es obligatorio");
              return;
            }
            mut.mutate();
          }}
          disabled={mut.isPending}
        >
          {mut.isPending ? "Finalizando…" : "Finalizar"}
        </Button>
      </div>
    </div>
  );
}

function CancelarForm({
  ordenId,
  onDone,
  onCancel,
}: {
  ordenId: number;
  onDone: () => void;
  onCancel: () => void;
}): JSX.Element {
  const toast = useToast();
  const [motivo, setMotivo] = useState("");

  const mut = useMutation({
    mutationFn: () => cancelarOrden(ordenId, motivo.trim()),
    onSuccess: () => {
      toast.success("OT cancelada");
      onDone();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "No se pudo cancelar"),
  });

  return (
    <div className="space-y-2 rounded-md border border-rose-200 bg-rose-50 p-3">
      <div className="text-sm font-medium text-rose-900">Cancelar OT</div>
      <Input
        label="Motivo"
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        autoFocus
      />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="secondary" onClick={onCancel} disabled={mut.isPending}>
          Volver
        </Button>
        <Button
          size="sm"
          variant="destructive"
          onClick={() => {
            if (!motivo.trim()) {
              toast.error("Indica el motivo");
              return;
            }
            mut.mutate();
          }}
          disabled={mut.isPending}
        >
          {mut.isPending ? "Cancelando…" : "Cancelar OT"}
        </Button>
      </div>
    </div>
  );
}

function ConsumoForm({
  ordenId,
  onDone,
  onCancel,
}: {
  ordenId: number;
  onDone: () => void;
  onCancel: () => void;
}): JSX.Element {
  const toast = useToast();
  const [insumoId, setInsumoId] = useState("");
  const [bodegaId, setBodegaId] = useState("");
  const [cantidad, setCantidad] = useState("");

  const insumosQ = useQuery({ queryKey: ["inventario", "insumos"], queryFn: listarInsumos });
  const bodegasQ = useQuery({ queryKey: ["inventario", "bodegas"], queryFn: listarBodegas });

  const mut = useMutation({
    mutationFn: () =>
      agregarConsumo(ordenId, {
        insumo_id: Number(insumoId),
        bodega_id: Number(bodegaId),
        cantidad: Number(cantidad),
      }),
    onSuccess: () => {
      toast.success("Material consumido (se descontó del stock)");
      onDone();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "No se pudo registrar"),
  });

  const insumosActivos = (insumosQ.data ?? []).filter((i) => i.activo);
  const bodegas = bodegasQ.data ?? [];

  if (insumosQ.isPending || bodegasQ.isPending) {
    return (
      <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3">
        <Cargando inline />
      </div>
    );
  }

  // Sin insumos o sin bodegas el formulario sólo mostraría selects vacíos.
  if (insumosActivos.length === 0 || bodegas.length === 0) {
    return (
      <div className="space-y-2 rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm">
        <div className="font-medium text-neutral-800">Consumir material de bodega</div>
        {insumosActivos.length === 0 ? (
          <p className="text-neutral-700">
            No hay insumos en bodega. Créalos en{" "}
            <Link to="/insumos" className="font-medium text-brand-700 underline hover:text-brand-800">
              Bodega → Insumos
            </Link>
            .
          </p>
        ) : (
          <p className="text-neutral-700">
            No hay bodegas creadas. Créalas en{" "}
            <Link to="/bodega" className="font-medium text-brand-700 underline hover:text-brand-800">
              Bodega
            </Link>
            .
          </p>
        )}
        <div className="flex justify-end">
          <Button size="sm" variant="secondary" onClick={onCancel}>
            Cerrar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-md border border-neutral-200 bg-neutral-50 p-3">
      <div className="text-sm font-medium text-neutral-800">Consumir material de bodega</div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
        <Select label="Insumo" value={insumoId} onChange={(e) => setInsumoId(e.target.value)}>
          <option value="">— Elegir —</option>
          {insumosActivos.map((i) => (
            <option key={i.id} value={i.id}>
              {i.nombre} ({i.unidad_medida})
            </option>
          ))}
        </Select>
        <Select label="Bodega" value={bodegaId} onChange={(e) => setBodegaId(e.target.value)}>
          <option value="">— Elegir —</option>
          {bodegas.map((b) => (
            <option key={b.id} value={b.id}>
              {b.nombre}
            </option>
          ))}
        </Select>
        <Input
          label="Cantidad"
          type="number"
          min={0}
          value={cantidad}
          onChange={(e) => setCantidad(e.target.value)}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="secondary" onClick={onCancel} disabled={mut.isPending}>
          Cancelar
        </Button>
        <Button
          size="sm"
          onClick={() => {
            if (!insumoId || !bodegaId || Number(cantidad) <= 0) {
              toast.error("Completa insumo, bodega y cantidad");
              return;
            }
            mut.mutate();
          }}
          disabled={mut.isPending}
        >
          {mut.isPending ? "Registrando…" : "Consumir"}
        </Button>
      </div>
    </div>
  );
}
